<script setup lang="ts">
import FormGroup from "@/components/form/FormGroup.vue";
import { getMessageFromError } from "@/libs/errors";
import { platform } from "@/libs/environment";
import { useAuthStore } from "@/stores/auth";
import { computed, ref } from "vue";
import { useRouter } from "vue-router";

const registerRoute = computed(() =>
  platform !== "web" ? "/auth/mobile-register" : "/auth/register"
);
const showPassword = ref(false);
const loading = ref(false);
const errorMessage = ref("");
const email = ref("");
const password = ref("");
const authStore = useAuthStore();
const router = useRouter();
const login = async () => {
  if (loading.value) {
    return;
  }
  loading.value = true;
  try {
    errorMessage.value = "";
    await authStore.login(email.value, password.value);
    router.push("/");
  } catch (err) {
    errorMessage.value = getMessageFromError(err);
  }
  loading.value = false;
};
</script>

<template>
  <div class="login">
    <div class="container">
      <div class="content-center">
        <div class="row">
          <div class="col col-12 d-block">
            <h2 class="mb-4">
              <img src="@/assets/images/logo.svg" alt="logo" width="300" />
            </h2>
          </div>
          <div class="col col-12">
            <div class="card-plain">
              <div class="buttons mb-4">
                <p class="question mb-2">É o seu primeiro acesso?</p>
                <div class="d-grid gap-2">
                  <router-link
                    :to="registerRoute"
                    class="btn btn-primary btn-block mb-2"
                  >
                    Criar conta
                  </router-link>
                </div>
              </div>
              <div class="divider mb-3">
                <div class="line"></div>
                <div class="or">OU</div>
              </div>
              <form @submit.prevent="login">
                <div v-if="errorMessage" class="alert alert-danger">
                  {{ errorMessage }}
                </div>
                <p class="question mb-2">Já é credenciado?</p>
                <FormGroup label="E-mail" labelFor="email">
                  <input
                    id="email"
                    type="email"
                    class="form-control"
                    v-model="email"
                  />
                </FormGroup>
                <FormGroup label="Senha" labelFor="password" class="mb-4">
                  <input
                    id="password"
                    :type="showPassword ? 'text' : 'password'"
                    class="form-control"
                    v-model="password"
                  />
                  <template #append-slot>
                    <span
                      class="input-group-text"
                      :class="{ active: showPassword }"
                      @click="showPassword = !showPassword"
                    >
                      <svg
                        v-if="showPassword"
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        fill="currentColor"
                        class="bi bi-eye-slash"
                        viewBox="0 0 16 16"
                      >
                        <path
                          d="M13.359 11.238C15.06 9.72 16 8 16 8s-3-5.5-8-5.5a7.028 7.028 0 0 0-2.79.588l.77.771A5.944 5.944 0 0 1 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.134 13.134 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755-.165.165-.337.328-.517.486l.708.709z"
                        />
                        <path
                          d="M11.297 9.176a3.5 3.5 0 0 0-4.474-4.474l.823.823a2.5 2.5 0 0 1 2.829 2.829l.822.822zm-2.943 1.299.822.822a3.5 3.5 0 0 1-4.474-4.474l.823.823a2.5 2.5 0 0 0 2.829 2.829z"
                        />
                        <path
                          d="M3.35 5.47c-.18.16-.353.322-.518.487A13.134 13.134 0 0 0 1.172 8l.195.288c.335.48.83 1.12 1.465 1.755C4.121 11.332 5.881 12.5 8 12.5c.716 0 1.39-.133 2.02-.36l.77.772A7.029 7.029 0 0 1 8 13.5C3 13.5 0 8 0 8s.939-1.721 2.641-3.238l.708.709zm10.296 8.884-12-12 .708-.708 12 12-.708.708z"
                        />
                      </svg>
                      <svg
                        v-else
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        fill="currentColor"
                        class="bi bi-eye"
                        viewBox="0 0 16 16"
                      >
                        <path
                          d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8zM1.173 8a13.133 13.133 0 0 1 1.66-2.043C4.12 4.668 5.88 3.5 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.133 13.133 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12.5 8 12.5c-2.12 0-3.879-1.168-5.168-2.457A13.134 13.134 0 0 1 1.172 8z"
                        />
                        <path
                          d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zM4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0z"
                        />
                      </svg>
                    </span>
                  </template>
                </FormGroup>
                <div class="buttons d-grid gap-2">
                  <button
                    v-if="loading"
                    type="submit"
                    class="btn btn-secondary btn-block"
                    disabled
                  >
                    Carregando...
                  </button>
                  <button
                    v-else
                    type="submit"
                    class="btn btn-secondary btn-block"
                  >
                    Fazer Login
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.login {
  height: 100vh;
  max-height: 1050px;
  padding: 0;
  position: relative;
  background-color: #16161d;
}

.login:before {
  /* background-color: #a5c70f; */
  content: "";
  position: absolute;
  z-index: 1;
  width: 100%;
  height: 184px;
  left: 0;
}

.content-center {
  position: relative;
  margin: 0 auto;
  z-index: 99;
  padding: 56px 0 0;
}

h2 {
  font-size: 1.5625rem;
  margin: 0;
  padding: 1.5625rem 0 0.55rem;
  text-align: center;
}

h2 a {
  color: #030303;
  text-decoration: none;
}

.card-plain {
  margin: 0 auto 50px;
  border-radius: 0.5rem;
  max-width: 330px;
  padding: 20px;
  background: var(--white-ice);
  -webkit-box-shadow: 0 1px 2px 0 rgb(0 0 0 / 10%);
  box-shadow: 0 1px 2px 0 rgb(0 0 0 / 10%);
  color: var(--black-eigengrau);
}

.card-plain h1 {
  font-size: 1.5rem;
  line-height: 1.4em;
  margin-bottom: 20px;
  text-align: center;
}

.card-plain .form-control {
  background: #e3e1e4;
  border-color: #030303;
}

.card-plain .form-control:focus {
  box-shadow: rgba(82, 95, 26, 0.2) 0px 0px 0px 0.25rem;
}

.btn-check:focus + .btn,
.btn:focus {
  box-shadow: rgba(82, 95, 26, 0.2) 0px 0px 0px 0.25rem;
}

.btn-check:focus + .btn-primary,
.btn-primary:focus {
  border-color: #000;
}

.input-group-text {
  background-color: var(--white-ice);
  border: 1px solid var(--black-eigengrau);
}

.input-group-text.active {
  background-color: var(--black-eigengrau);
  color: var(--white-ice);
}

.question {
  margin: 0;
  padding: 0;
  font-weight: 600;
}

.divider {
  text-align: center;
  background-color: var(--white-ice);
  font-weight: 400;
  color: var(--dark-grey-alpha);
}

.or {
  display: table;
  background-color: var(--white-ice);
  padding: 0 5px;
  margin-left: calc(50% - 31px / 2);
  margin-top: -11px;
  font-weight: 600;
}

.line {
  border-top: 1px solid var(--dark-grey-alpha);
}

.btn-primary {
  background-color: var(--highlight-color);
  border-color: var(--highlight-color);
  color: var(--white-ice);
  font-weight: 600;
}

.btn-secondary {
  /* outline: 2px solid var(--highlight-color);
  outline-offset: -2px; */
  border-color: var(--primary-color);
  background-color: var(--primary-color);
  color: var(--black-eigengrau);
  font-weight: 600;
}

.forgetPassword {
  font-size: 14px;
}

.forgetPassword a {
  color: var(--highlight-color);
  font-weight: 500;
  text-decoration: none;
}

.forgetPassword a:hover {
  text-decoration: underline;
}

.createAccount {
  font-size: 14px;
}

.createAccount a {
  color: var(--black-highlight);
  font-weight: 500;
  text-decoration: none;
}

.createAccount a:hover {
  text-decoration: underline;
}
</style>
